/**
 * Public mobile menu - always-mounted panel portaled to document.body.
 * Uses CSS @keyframes for slide-in (reliable on public pages where Chakra
 * Drawer enter animation is skipped due to mount + hook re-renders).
 */

"use client";

import { Box, Flex, VStack } from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { type AnimationEvent, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { HiOutlineArrowRightOnRectangle } from "react-icons/hi2";
import {
  ConfirmationModal,
  Link,
  LOGOUT_CONFIRMATION_LOADING,
} from "@/components";
import { LogoutTransitionOverlay } from "@/components/auth/LogoutTransitionOverlay";
import { getPublicNavbarLinks } from "@/components/shared/sidebar/linkItems";
import {
  SidebarNavIcon,
  SidebarNavItemLabel,
  sidebarNavRowAlign,
} from "@/components/shared/sidebar/SidebarNavItemLabel";
import { SidebarThemeToggle } from "@/components/shared/sidebar/SidebarThemeToggle";
import { getSidebarLogoutButtonStyles } from "@/components/shared/sidebar/sidebarLogoutButtonStyles";
import { TenantLogo } from "@/components/shared/TenantLogo";
import {
  useMediaAssetGet,
  usePresignedImageErrorRecovery,
} from "@/features/media/hooks";
import {
  useLogout,
  usePublicLocationsNavLink,
  usePublicTicketsNavLink,
  useTenantData,
} from "@/hooks";
import { DASHBOARD_SIDEBAR_WIDTH_EXPANDED } from "@/lib/layout/dashboard-sidebar";
import { FULL_VIEWPORT_HEIGHT } from "@/lib/layout/viewport";
import { useThemeTokens } from "@/lib/theme/tokens";
import { PageTypePortal } from "@/lib/types";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import { EntryType, MediaGroup } from "@/openapi/generated/models";

const MENU_Z_INDEX = 1400;
const ANIMATION_MS = 300;
const EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

type PanelPhase = "closed" | "entering" | "open" | "exiting";

type PublicMobileMenuDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export const PublicMobileMenuDrawer = ({
  isOpen,
  onClose,
}: PublicMobileMenuDrawerProps) => {
  const pathname = usePathname();
  const theme = useThemeTokens();
  const { data: session } = useSession();
  const user = session?.user ?? null;
  const isMember = !!user?.isMember;
  const { tenantData, tenantId } = useTenantData();
  const tenantName = getTenantDisplayName(tenantData);
  const locationsNav = usePublicLocationsNavLink();
  const ticketsNav = usePublicTicketsNavLink();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [phase, setPhase] = useState<PanelPhase>("closed");
  const {
    isLogoutModalOpen,
    onLogoutModalOpen,
    onLogoutModalClose,
    handleLogout,
    isLoggingOut,
    showLogoutOverlay,
    logoUrl: logoutLogoUrl,
    tenantData: logoutTenantData,
  } = useLogout({
    callbackUrl: PageTypePortal.Login,
    logoRequireAuth: false,
  });

  const { url: logoUrl, refetch: refetchLogo } = useMediaAssetGet(
    {
      tenantId,
      ownerType: EntryType.Tenant,
      ownerId: tenantId,
      mediaGroup: MediaGroup.Logo,
    },
    { enabled: !!tenantId, requireAuth: false, refetchOnMount: false },
  );
  const handleLogoError = usePresignedImageErrorRecovery(logoUrl, refetchLogo);

  const navItems = useMemo(
    () =>
      getPublicNavbarLinks(isMember, pathname ?? "", {
        locationsNav,
        ticketsNav,
      }),
    [isMember, pathname, locationsNav, ticketsNav],
  );

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setPhase(isOpen ? "open" : "closed");
      return;
    }

    if (isOpen) {
      setPhase((current) =>
        current === "open" || current === "entering" ? current : "entering",
      );
      return;
    }

    setPhase((current) =>
      current === "closed" || current === "exiting" ? current : "exiting",
    );
  }, [isOpen]);

  const isInteractive =
    phase === "open" || phase === "entering" || phase === "exiting";

  useEffect(() => {
    if (!isInteractive) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isInteractive]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handlePanelAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;

    setPhase((current) => {
      if (current === "entering") return "open";
      if (current === "exiting") return "closed";
      return current;
    });
  };

  const isDark = theme.mode === "dark";
  const overlayBg = isDark ? "rgba(0,0,0,0.8)" : "rgba(11,13,16,0.6)";

  const panelAnimation =
    phase === "entering"
      ? `public-mobile-menu-enter ${ANIMATION_MS}ms ${EASING} forwards`
      : phase === "exiting"
        ? `public-mobile-menu-exit ${ANIMATION_MS}ms ${EASING} forwards`
        : undefined;

  const panelTransform =
    phase === "open"
      ? "translate3d(0, 0, 0)"
      : phase === "closed"
        ? "translate3d(-100%, 0, 0)"
        : undefined;

  const overlayOpacity = phase === "closed" || phase === "exiting" ? 0 : 1;

  const overlayTransition =
    phase === "closed" ? "none" : `opacity ${ANIMATION_MS}ms ${EASING}`;

  if (!portalTarget) {
    return (
      <>
        <ConfirmationModal
          data-testid="public-mobile-menu-logout-dialog"
          isOpen={isLogoutModalOpen}
          onClose={onLogoutModalClose}
          onConfirm={handleLogout}
          isLoading={isLoggingOut}
          title="Confirm Logout"
          message="Are you sure you want to log out?"
          confirmText="Log out"
          confirmColorScheme="red"
          {...LOGOUT_CONFIRMATION_LOADING}
        />
        <LogoutTransitionOverlay
          isVisible={showLogoutOverlay}
          logoUrl={logoutLogoUrl}
          tenantData={logoutTenantData}
        />
      </>
    );
  }

  return (
    <>
      {createPortal(
        <Box
          data-testid="public-mobile-menu"
          position="fixed"
          inset={0}
          zIndex={MENU_Z_INDEX}
          pointerEvents={isInteractive ? "auto" : "none"}
          display={{ base: "block", lg: "none" }}
          aria-hidden={!isInteractive}
        >
          <Box
            position="absolute"
            inset={0}
            bg={overlayBg}
            backdropFilter="blur(8px)"
            style={{
              opacity: overlayOpacity,
              transition: overlayTransition,
            }}
            onClick={onClose}
            aria-hidden="true"
          />

          <Box
            as="nav"
            aria-label="Mobile navigation"
            position="fixed"
            top={0}
            left={0}
            h={FULL_VIEWPORT_HEIGHT}
            w={`${DASHBOARD_SIDEBAR_WIDTH_EXPANDED}px`}
            maxW="85vw"
            bg={isDark ? "rgba(11,13,16,0.95)" : "rgba(255,255,255,0.95)"}
            borderRightWidth="1px"
            borderColor={theme.borderGlass.default}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            boxShadow="2xl"
            onAnimationEnd={handlePanelAnimationEnd}
            sx={{
              "@keyframes public-mobile-menu-enter": {
                from: { transform: "translate3d(-100%, 0, 0)" },
                to: { transform: "translate3d(0, 0, 0)" },
              },
              "@keyframes public-mobile-menu-exit": {
                from: { transform: "translate3d(0, 0, 0)" },
                to: { transform: "translate3d(-100%, 0, 0)" },
              },
              ...(panelAnimation ? { animation: panelAnimation } : {}),
              ...(panelTransform ? { transform: panelTransform } : {}),
              willChange: "transform",
            }}
          >
            <Box
              py={6}
              px={4}
              flex="1"
              minH={0}
              display="flex"
              flexDirection="column"
              gap={6}
            >
              <Flex align="center" justify="flex-start">
                <TenantLogo
                  width={48}
                  height={43}
                  tenantData={tenantData}
                  src={logoUrl}
                  alt={tenantName}
                  priority
                  onError={handleLogoError}
                />
              </Flex>

              <VStack
                align="stretch"
                spacing={2}
                flex="1"
                minH={0}
                overflowY="auto"
                sx={{ WebkitOverflowScrolling: "touch" }}
              >
                {navItems.map((item) => {
                  const isActive = item.active;

                  return (
                    <Link key={item.href} href={item.href} onClick={onClose}>
                      <Flex
                        align={sidebarNavRowAlign(false)}
                        justify="flex-start"
                        px={3}
                        py={2.5}
                        w="100%"
                        borderRadius="8px"
                        gap={3}
                        bg={isActive ? theme.accent.primaryBg : "transparent"}
                        color={
                          isActive ? theme.accent.primary : theme.text.secondary
                        }
                        transition="background-color 0.18s ease-out, color 0.18s ease-out"
                      >
                        <SidebarNavIcon as={item.icon} />
                        <SidebarNavItemLabel isCollapsed={false}>
                          {item.label}
                        </SidebarNavItemLabel>
                      </Flex>
                    </Link>
                  );
                })}

                <Box
                  borderTopWidth="1px"
                  borderTopColor={theme.borderGlass.default}
                  pt={4}
                  mt={1}
                  w="100%"
                >
                  <SidebarThemeToggle />
                  {user && (
                    <Box pt={4} w="100%">
                      <Flex
                        as="button"
                        type="button"
                        align="center"
                        justify="flex-start"
                        px={3}
                        py={2.5}
                        w="100%"
                        borderRadius="8px"
                        gap={3}
                        {...getSidebarLogoutButtonStyles(theme, false)}
                        onClick={onLogoutModalOpen}
                      >
                        <SidebarNavIcon as={HiOutlineArrowRightOnRectangle} />
                        <SidebarNavItemLabel compact>
                          Log out
                        </SidebarNavItemLabel>
                      </Flex>
                    </Box>
                  )}
                </Box>
              </VStack>
            </Box>
          </Box>
        </Box>,
        portalTarget,
      )}

      <ConfirmationModal
        data-testid="public-mobile-menu-logout-dialog"
        isOpen={isLogoutModalOpen}
        onClose={onLogoutModalClose}
        onConfirm={handleLogout}
        isLoading={isLoggingOut}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
        confirmText="Log out"
        confirmColorScheme="red"
        {...LOGOUT_CONFIRMATION_LOADING}
      />
      <LogoutTransitionOverlay
        isVisible={showLogoutOverlay}
        logoUrl={logoutLogoUrl}
        tenantData={logoutTenantData}
      />
    </>
  );
};
